import axios, { AxiosResponse } from 'axios'

import { RequestQuery } from '@models/apiBase'
import { Response } from '@models/apiBase'
import { WithFilter } from '@models/apiBase'
import { BloodTypeEnum, ReligionEnum } from '@models/base'
import { GenderEnum } from '@models/base'
import { MaritalStatusEnum } from '@models/base'
import { ID } from '@models/base'
import {
  PatientDoctorPreviewModel,
  PatientMedicalSummaryModel,
  PatientModel,
  PatientOptionModel,
} from '@models/patient'
import { PatientMedicalNotesModel } from '@models/patientNote'

import { API_URL } from '@/constants/constant'

const PATIENTS_URL = `${API_URL}/patients`

type ResponsePatients = Response<WithFilter<Array<PatientModel>>>
type ResponsePatient = Response<{ patient: PatientModel }>
type ResponsePatientOptions = Response<WithFilter<Array<PatientOptionModel>>>
type ResponsePatientDoctorsPreview = Response<WithFilter<Array<PatientDoctorPreviewModel>>>

type ResponsePatientMedicalNotes = Response<{ patient: PatientMedicalNotesModel }>

type ResponsePatientMedicalSummary = Response<{
  patient: PatientMedicalSummaryModel
}>

export type PayloadUpdatePatientNote = {
  medical_notes: string
}

export type PayloadPatientMedicalSummary = {
  medical_summary?: string
}

export type PayloadCreatePatient = {
  birth_date: string
  blood_type: BloodTypeEnum
  business_phone_number: string | null
  family_card_number: string | null
  first_name: string
  gender: GenderEnum
  home_phone_number: string | null
  is_asian: boolean
  is_organ_donor: boolean | null
  is_signed_satusehat_consent: boolean
  language: string | null
  last_name: string | null
  living_address_1: string | null
  living_address_2: string | null
  living_rt: string | null
  living_rw: string | null
  living_country: string | null
  living_first_level_administrative_division_id: ID
  living_fourth_level_administrative_division_id: ID
  living_postal_code: string | null
  living_second_level_administrative_division_id: ID
  living_third_level_administrative_division_id: ID
  marital_status: MaritalStatusEnum | null
  medical_notes: string | null
  mobile_phone_number: string | null
  nric_address_1: string | null
  nric_address_2: string | null
  nric_rt: string | null
  nric_rw: string | null
  nric_country: string | null
  nric_file_id: ID
  nric_first_level_administrative_division_id: ID
  nric_fourth_level_administrative_division_id: ID
  nric_number: string | null
  nric_postal_code: string
  nric_second_level_administrative_division_id: ID
  nric_third_level_administrative_division_id: ID
  passport_number: string | null
  religion: ReligionEnum
}

export type PayloadUpdatePatient = {
  birth_date: string
  blood_type: BloodTypeEnum
  business_phone_number: string | null
  family_card_number: string | null
  first_name: string
  gender: GenderEnum
  home_phone_number: string | null
  is_asian: boolean
  is_organ_donor: boolean | null
  is_signed_satusehat_consent: boolean
  language: string | null
  last_name: string | null
  living_address_1: string | null
  living_address_2: string | null
  living_rt: string | null
  living_rw: string | null
  living_country: string | null
  living_first_level_administrative_division_id: ID
  living_fourth_level_administrative_division_id: ID
  living_postal_code: string | null
  living_second_level_administrative_division_id: ID
  living_third_level_administrative_division_id: ID
  marital_status: MaritalStatusEnum | null
  medical_notes: string | null
  mobile_phone_number: string | null
  nric_address_1: string | null
  nric_address_2: string | null
  nric_rt: string | null
  nric_rw: string | null
  nric_country: string | null
  nric_file_id: ID
  nric_first_level_administrative_division_id: ID
  nric_fourth_level_administrative_division_id: ID
  nric_number: string | null
  nric_postal_code: string
  nric_second_level_administrative_division_id: ID
  nric_third_level_administrative_division_id: ID
  passport_number: string | null
  religion: ReligionEnum
}

interface IPatientsAPI {
  filter: (query: RequestQuery) => Promise<ResponsePatients>

  get: (id: ID) => Promise<ResponsePatient>

  create: (payload: PayloadCreatePatient) => Promise<ResponsePatient>

  update: (id: ID, payload: PayloadUpdatePatient) => Promise<ResponsePatient>

  getOptionsForAppointmentForm: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForTaskForm: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForPatientRelativeForm: (query: RequestQuery<{ patient_id: ID }>) => Promise<ResponsePatientOptions>

  getOptionsForPatientVisitForm: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForPatientVisitFilter: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForPatientVitalCheckForm: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForPatientVitalCheckFilter: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForQueueNumberGenerateForm: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForPatientDoctorForm: (query: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForBookingForm: (payload: RequestQuery) => Promise<ResponsePatientOptions>

  getOptionsForDiagnosticAncillaryForm: (payload: RequestQuery) => Promise<ResponsePatientOptions>

  updateMedicalSummary: (id: ID, payload: PayloadPatientMedicalSummary) => Promise<ResponsePatient>

  getMedicalSummary: (id: ID) => Promise<ResponsePatientMedicalSummary>

  updateMedicalNote: (id: ID, payload: PayloadUpdatePatientNote) => Promise<ResponsePatientMedicalNotes>

  getMedicalNote: (id: ID) => Promise<ResponsePatientMedicalNotes>

  getMedicalRecord: (patientVisitId: ID) => Promise<ResponsePatient>

  filterPatientDoctor: (query: RequestQuery) => Promise<ResponsePatientDoctorsPreview>
}

export const PatientsAPI: IPatientsAPI = {
  filter: async (query) => {
    return axios.post(`${PATIENTS_URL}/filter`, query).then((r: AxiosResponse<ResponsePatients>) => r.data)
  },

  get: async (id) => {
    return axios.get(`${PATIENTS_URL}/${id}`).then((r: AxiosResponse<ResponsePatient>) => r.data)
  },

  create: async (payload: PayloadCreatePatient) => {
    return axios.post(PATIENTS_URL, payload).then((r: AxiosResponse<ResponsePatient>) => r.data)
  },

  update: async (id, payload) => {
    return axios.put(`${PATIENTS_URL}/${id}`, payload).then((r: AxiosResponse<ResponsePatient>) => r.data)
  },

  getOptionsForAppointmentForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/appointment-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForTaskForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/task-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForPatientRelativeForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/patient-relative-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForPatientVisitForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/patient-visit-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForPatientVisitFilter: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/patient-visit-filter`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForPatientVitalCheckForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/patient-vital-check-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForPatientVitalCheckFilter: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/patient-vital-check-filter`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForQueueNumberGenerateForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/queue-number-generate-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForPatientDoctorForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/patient-doctor-form`, query).then((r: AxiosResponse) => {
      return r.data
    })
  },

  getOptionsForBookingForm: async (query) => {
    return axios.post(`${PATIENTS_URL}/options/booking-form`, query).then((r: AxiosResponse) => {
      return r.data
    })
  },

  getOptionsForDiagnosticAncillaryForm: async (payload) => {
    return axios.post(`${PATIENTS_URL}/options/diagnostic-ancillary-form`, payload).then((r) => r.data)
  },

  updateMedicalSummary: async (id, payload) => {
    return axios
      .patch(`${PATIENTS_URL}/${id}/medical-summary`, payload)
      .then((r: AxiosResponse<ResponsePatient>) => r.data)
  },

  getMedicalSummary: async (id) => {
    return axios
      .get(`${PATIENTS_URL}/${id}/medical-summary`)
      .then((r: AxiosResponse<ResponsePatientMedicalSummary>) => r.data)
  },

  updateMedicalNote: async (id, payload) => {
    return axios
      .patch(`${PATIENTS_URL}/${id}/medical-notes`, payload)
      .then((r) => r.data)
  },

  getMedicalNote: async (id) => {
    return axios.get(`${PATIENTS_URL}/${id}/medical-notes`).then((r) => r.data)
  },

  getMedicalRecord: async (patientVisitId) => {
    return axios
      .get(`${PATIENTS_URL}/medical-record/${patientVisitId}`)
      .then((r: AxiosResponse<ResponsePatient>) => r.data)
  },

  filterPatientDoctor: async (query) => {
    return axios
      .post(`${PATIENTS_URL}/filter/patient-doctor`, query)
      .then((r: AxiosResponse<ResponsePatientDoctorsPreview>) => r.data)
  },
}
